import React from 'react';
import { Link } from 'react-router-dom';
import { handlePhoneClick } from '../../../utils/trackPhoneCall';
import { handleQuoteClick } from '../../../utils/quoteCta';
import { siteConfig } from '../../../config/site';
import ApplianceBookingModal from './ApplianceBookingModal';
import { climatizacaoServicesList } from '../../../data/climatizacaoServices';
import type { Zona } from '../../../data/zonas';
import { limpezaServicesList } from '../../../data/limpezaServices';

const Services = ({ zona }: { zona?: Zona } = {}) => {
  const ServiceRow: React.FC<{ service: any; index: number }> = ({ service, index }) => {
    const rowRef = React.useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = React.useState(false);
    React.useEffect(() => {
      if (!rowRef.current) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              setVisible(true);
              obs.disconnect();
            }
          });
        },
        { threshold: 0.2 }
      );
      obs.observe(rowRef.current);
      return () => obs.disconnect();
    }, []);

    const imageFrom = index % 2 === 1 ? 'translate-x-8' : '-translate-x-8';
    const textFrom = index % 2 === 1 ? '-translate-x-8' : 'translate-x-8';

    return (
      <div ref={rowRef} className={`mb-20 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Image */}
          <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
            <div className={`relative rounded-2xl overflow-hidden shadow-xl transition-all duration-700 aspect-square w-full sm:w-11/12 lg:w-[70%] mx-auto ${visible ? 'opacity-100 translate-x-0' : `opacity-0 ${imageFrom}`
              }`}>
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover object-[center_30%]"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>

          {/* Content */}
          <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
            <div className={`space-y-4 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : `opacity-0 ${textFrom}`
              }`}>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {service.title}
                </h3>
                <p className="text-base text-blue-600 font-semibold mb-2">
                  {service.subtitle}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="pt-2 flex flex-wrap gap-3">
                <Link
                  to={service.path || `/servico/${service.id}`}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 inline-flex items-center gap-2"
                >
                  <i className="ri-information-line"></i>
                  Saiba mais
                </Link>
                <a
                  href={isClimatizacaoSite ? '#orcamento' : `tel:${siteConfig.phone}`}
                  onClick={isClimatizacaoSite ? handleQuoteClick : handlePhoneClick}
                  className="bg-white hover:bg-gray-50 text-blue-600 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 inline-flex items-center gap-2 border-2 border-blue-600"
                >
                  <i className={isClimatizacaoSite ? 'ri-mail-send-line' : 'ri-phone-line'}></i>
                  Pedir Orçamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const isDesentupimentosSite = siteConfig.domain === 'alphadesentupimentos.pt';
  const isPortoSite = siteConfig.domain === 'alphacanalizacoesporto.pt';
  const isEletricaSite = siteConfig.domain === 'alphaeletrica.pt' || siteConfig.domain === 'alphaeletric.pt';
  const isEletrodomesticosSite = siteConfig.domain === 'alphaservices.pt';
  const isClimatizacaoSite = siteConfig.domain === 'alphaclimatizacao.pt';
  const isCleanSite = siteConfig.domain === 'alphaclean.pt';
  // Nas páginas de zona a localidade substitui a cidade
  const cityName = zona ? zona.nome : siteConfig.address.city;
  const prep = zona ? zona.preposicao : 'em';

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedService, setSelectedService] = React.useState<any>(null);

  const openBooking = (service: any) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const services: any[] = isCleanSite ? limpezaServicesList.map((s) => ({
    id: s.id,
    path: `/${s.slug}`,
    title: s.title,
    subtitle: s.subtitle,
    description: s.description,
    image: s.image,
    features: s.features || []
  })) : isClimatizacaoSite ? climatizacaoServicesList.map((s) => ({
    id: s.id,
    path: zona ? `/${s.slug}-${zona.slug}` : `/${s.slug}`,
    title: zona ? `${s.tituloLocal} ${zona.preposicao} ${zona.nome}` : s.title,
    subtitle: s.subtitle,
    description: zona ? (zona.notasPorServico[s.id] || s.description) : s.description,
    image: s.image,
    features: []
  })) : isDesentupimentosSite ? [
    {
      id: 'esgotos',
      title: 'Desentupimento de Esgotos em Lisboa',
      subtitle: 'Serviço profissional para habitações, condomínios e empresas',
      description: 'A Alpha Desentupimentos é especialista em desentupimento de esgotos em Lisboa, atuando de forma rápida e eficaz em habitações, condomínios, empresas e estabelecimentos comerciais. Os entupimentos na rede de esgotos podem causar retornos de água, maus odores, infiltrações e danos estruturais.',
      image: '/images/desentupimento-esgoto-real.webp',
      features: []
    },
    {
      id: 'sanitas',
      title: 'Desentupimento de Sanitas em Lisboa',
      subtitle: 'Serviço profissional para resolver situações urgentes',
      description: 'A Alpha Desentupimentos presta um serviço profissional de desentupimento de sanitas em Lisboa, com intervenção rápida e eficaz para resolver situações urgentes em casas, apartamentos, condomínios, restaurantes e espaços comerciais.',
      image: '/images/desentupimento-sanitas-real.webp',
      features: []
    },
    {
      id: 'lavatorios-pias',
      title: 'Desentupimento de Lavatórios e Pias em Lisboa',
      subtitle: 'Resolvendo problemas comuns em cozinhas e casas de banho',
      description: 'A Alpha Desentupimentos é especialista em desentupimento de lavatórios e pias em Lisboa, resolvendo problemas comuns em cozinhas e casas de banho de forma rápida e eficaz. Quando a água demora a escoar ou começa a voltar, é sinal claro de obstrução nas tubagens.',
      image: '/images/desentupimento-pias-real.webp',
      features: []
    },
    {
      id: 'colunas-prediais',
      title: 'Desentupimento de Colunas Prediais em Lisboa',
      subtitle: 'Prestando apoio técnico a condomínios e prédios',
      description: 'A Alpha Desentupimentos é especializada em desentupimento de colunas prediais em Lisboa, prestando apoio técnico a condomínios, administradores, prédios residenciais e edifícios comerciais. Os entupimentos nas colunas de esgoto são problemas graves, pois afetam vários apartamentos em simultâneo.',
      image: '/images/desentupimento-torre-real.webp',
      features: []
    },
    {
      id: 'hidrojato',
      title: 'Desentupimento com Hidrojato em Lisboa',
      subtitle: 'Indicado para obstruções difíceis e entupimentos recorrentes',
      description: 'A Alpha Desentupimentos oferece serviço profissional de desentupimento com hidrojato em Lisboa, indicado para obstruções difíceis e entupimentos recorrentes nas tubagens. O hidrojato utiliza jato de água de alta pressão para remover gordura solidificada, incrustações e resíduos acumulados.',
      image: '/images/desentupimento-hidrojato-real.webp',
      features: []
    },
    {
      id: 'video-inspecao',
      title: 'Vídeo Inspeção de Tubulações em Lisboa',
      subtitle: 'Diagnóstico preciso sem necessidade de obras',
      description: 'A vídeo inspeção de tubulações é uma das formas mais eficazes e precisas de diagnosticar problemas na rede de esgoto sem necessidade de obras. A Alpha Desentupimentos disponibiliza este serviço em Lisboa, utilizando equipamentos modernos e técnicos especializados.',
      image: '/images/video-inspecao-real.webp',
      features: []
    }
  ] : isEletricaSite ? [
    {
      id: 'reparacao-eletrica',
      title: 'Reparação Elétrica',
      subtitle: 'Corrigimos falhas elétricas com rapidez e segurança',
      description: 'Corrigimos falhas elétricas com rapidez e segurança, garantindo a estabilidade do seu sistema elétrico e a proteção de sua casa ou empresa. Atendimento urgente para todos os tipos de avarias.',
      image: '/images/reparacao-eletrica.webp',
      features: []
    },
    {
      id: 'iluminacao',
      title: 'Iluminação',
      subtitle: 'Instalamos e otimizamos sistemas de iluminação',
      description: 'Instalamos e otimizamos sistemas de iluminação para residências e empresas, garantindo eficiência, economia de energia e um ambiente bem iluminado e seguro.',
      image: '/images/iluminacao-led.webp',
      features: []
    },
    {
      id: 'instalacao-eletrica',
      title: 'Instalação Elétrica',
      subtitle: 'Implementamos sistemas elétricos eficientes e seguros',
      description: 'Implementamos sistemas elétricos eficientes e seguros, garantindo instalações modernas e confiáveis para residências e empresas, sempre seguindo as normas técnicas e padrões de qualidade.',
      image: '/images/instalacao-eletrica-real.webp',
      features: []
    },
    {
      id: 'quadro-eletrico',
      title: 'Quadro Elétrico',
      subtitle: 'Instalamos e substituímos quadros elétricos',
      description: 'Instalamos e substituímos quadros elétricos com segurança e eficiência, garantindo distribuição elétrica estável e proteção contra sobrecargas para residências e empresas.',
      image: '/images/quadro-eletrico.webp',
      features: []
    },
    {
      id: 'climatizacao-automacao',
      title: 'Climatização',
      subtitle: 'Ar condicionado e ventilação',
      description: 'Soluções completas de conforto: instalação e reparação de ar condicionado e ventilação.',
      image: '/images/climatizacao-hero.webp',
      features: []
    },
    {
      id: 'reparacao-ar-condicionado',
      title: 'Reparação de Ar Condicionado',
      subtitle: 'Manutenção, recarga de gás e reparação de avarias',
      description: 'Serviço especializado de reparação e manutenção de ar condicionado em Lisboa. Diagnóstico rápido, recarga de gás, limpeza de filtros e reparação de avarias em todas as marcas. Atendimento urgente 24h.',
      image: '/images/ar-condicionado-moderno.webp',
      features: []
    },
    {
      id: 'seguranca',
      title: 'Segurança Eletrónica',
      subtitle: 'Vídeo porteiros e automação',
      description: 'Proteja a sua casa com vídeo porteiros, fechaduras eletrónicas, portões automáticos e controlo de acessos.',
      image: '/images/video-porteiro.webp',
      features: []
    },
    {
      id: 'exterior-mobilidade',
      title: 'Exterior e Mobilidade',
      subtitle: 'Carregadores elétricos, portões e jardins',
      description: 'Wallbox para carros elétricos, automação de portões de garagem e sistemas de rega/iluminação para jardim.',
      image: '/images/exterior-hero.webp',
      features: []
    },
    {
      id: 'energia-renovavel',
      title: 'Energias Renováveis',
      subtitle: 'Painéis solares e bombas de calor',
      description: 'Produza a sua própria energia e poupe na fatura com painéis solares fotovoltaicos e bombas de calor de alta eficiência.',
      image: '/images/energia-hero.webp',
      features: []
    },
    {
      id: 'estores',
      title: 'Estores e Persianas',
      subtitle: 'Instalação, Motorização e Reparação',
      description: 'Serviço especializado em estores: reparação rápida, motorização de estores manuais e instalação de novos estores.',
      image: '/images/estores-hero.webp',
      features: []
    }
  ] : isEletrodomesticosSite ? [
    {
      id: 'placa-eletrica',
      title: 'Placa Elétrica (Indução/Elétrica)',
      subtitle: 'Instalação Standard',
      price: 69.90,
      description: 'Instalação profissional de placa de indução ou elétrica com verificação de segurança.',
      image: '/images/alpha services/Placa Elétrica.webp',
      features: []
    },
    {
      id: 'forno-eletrico',
      title: 'Forno Elétrico',
      subtitle: 'Instalação Standard',
      price: 69.90,
      description: 'Instalação de forno elétrico encastrado ou de bancada.',
      image: '/images/alpha services/forno_eletrico_instalacao.webp',
      features: []
    },
    {
      id: 'fogao-eletrico',
      title: 'Fogão Elétrico',
      subtitle: 'Instalação Standard',
      price: 79.90,
      description: 'Instalação de fogão elétrico até 1,5m de cabo elétrico.',
      image: '/images/alpha services/fogao eletrico.webp',
      features: []
    },
    {
      id: 'microondas',
      title: 'Microondas',
      subtitle: 'Instalação Standard',
      price: 59.90,
      description: 'Instalação de microondas de encastre ou livre instalação.',
      image: '/images/alpha services/microondas.webp',
      features: []
    },
    {
      id: 'maquina-loica',
      title: 'Máquina de Lavar Loiça',
      subtitle: 'Instalação Standard',
      price: 89.00,
      hasEncastreOption: true,
      description: 'Instalação de máquina de lavar loiça com ligações de água e esgoto.',
      image: '/images/alpha services/Máquina de Lavar Loiça.webp',
      features: []
    },
    {
      id: 'maquina-roupa',
      title: 'Máquina de Lavar Roupa',
      subtitle: 'Instalação Standard',
      price: 89.00,
      hasEncastreOption: true,
      description: 'Instalação de máquina de lavar roupa com ligações de água e esgoto.',
      image: '/images/alpha services/Máquina de Lavar Roupa.webp',
      features: []
    },
    {
      id: 'frigorifico',
      title: 'Frigorífico',
      subtitle: 'Instalação Standard',
      price: 99.00,
      description: 'Instalação de frigorífico simples ou americano com ligação de água se necessário.',
      image: '/images/alpha services/frigorifico.webp',
      features: []
    },
    {
      id: 'termoacumulador',
      title: 'Termoacumulador',
      subtitle: 'Instalação Standard',
      price: 89.90,
      description: 'Instalação de termoacumulador com fixação e ligações hidráulicas.',
      image: '/images/alpha services/Termoacumulador.webp',
      features: []
    }
  ] : [
    {
      id: 'fugas',
      title: 'Detecção e Reparação de Fugas de Água',
      subtitle: 'Detecção de fugas com equipamento profissional - Reparação urgente',
      description: `Detetamos e reparamos fugas de água com equipamento profissional de última geração. Utilizamos tecnologia avançada para localizar fugas ocultas em paredes, pavimentos e canos sem necessidade de quebrar tudo. Serviço de detecção não invasiva, reparação rápida e eficiente, técnicos especializados em ${cityName}, diagnóstico preciso, garantia no serviço e preço transparente. Se procura "detecção de fugas ${cityName}", "reparar fugas de água" ou "canalizador para fugas", fale connosco e resolvemos rapidamente.`,
      image: '/images/fuga-detecao.webp',
      features: []
    },
    {
      id: 'reparacao',
      title: 'Reparação de Canalização',
      subtitle: 'Canos partidos, torneiras avariadas? Reparação urgente 24h',
      description: `Reparamos canos partidos, torneiras avariadas, autoclismos com problemas e toda a canalização doméstica e comercial. Atendimento rápido com técnicos locais em ${cityName}, diagnóstico no local, peças compatíveis de alta qualidade, garantia em serviço e preço transparente. Se procura "reparação canalização urgente ${cityName}" ou "canalizador para reparações", fale connosco e resolvemos rapidamente.`,
      image: '/images/reparacao.webp',
      features: []
    },
    {
      id: 'instalacao',
      title: 'Instalação de Canalização',
      subtitle: 'Instalação nova com garantia e acabamento profissional',
      description: `Instalamos canalização nova para casas, apartamentos e estabelecimentos comerciais. Fazemos instalação de canos, torneiras, autoclismos, máquinas de lavar, esquentadores e toda a infraestrutura hidráulica. Equipa própria, materiais de qualidade, acabamento profissional, garantia em todos os serviços e preço claro. Se procura "instalação canalização ${cityName}" ou "canalizador para obra nova", estamos prontos para ajudar.`,
      image: '/images/instalacao.webp',
      features: []
    },
    {
      id: 'manutencao',
      title: 'Manutenção Preventiva',
      subtitle: 'Evite problemas com manutenção programada',
      description: `Prolongue a vida útil da sua canalização com um plano de manutenção preventiva: inspeção de canos, verificação de pressão, limpeza de sifões, verificação de fugas e teste de equipamentos. Reduz avarias inesperadas, evita custos elevados e melhora o desempenho. Serviço indicado para casas, condomínios e estabelecimentos comerciais em ${cityName}. Procura "manutenção canalização" ou "inspeção de canos"? Conte com a nossa equipa e receba relatório do estado após cada visita.`,
      image: '/images/manutencao-preventiva-real.webp',
      features: []
    },
    {
      id: 'remodelacao',
      title: 'Remodelação de Casas de Banho',
      subtitle: 'Remodelação completa com design moderno e funcional',
      description: `Especialistas em remodelação completa de casas de banho em ${cityName}. Fazemos renovação total com design moderno, instalação de novos sanitários, chuveiros, banheiras, azulejos, pavimentos e toda a canalização. Planeamento personalizado, materiais de qualidade, acabamento profissional, garantia em todos os serviços e orçamento transparente. Se procura "remodelação casa de banho ${cityName}", "renovação WC" ou "casa de banho moderna", estamos prontos para transformar o seu espaço.`,
      image: '/images/remodelacao.webp',
      features: []
    }
  ];

  return (
    <section id="servicos" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {isCleanSite ? `Serviços de Limpeza em ${cityName}` :
              isClimatizacaoSite ? `Ar Condicionado e Climatização ${prep} ${cityName}` :
              isEletricaSite ? `Serviços de Eletricista em ${cityName}` :
                isDesentupimentosSite ? `Serviços de Desentupimentos em ${cityName}` :
                  isEletrodomesticosSite ? `Instalação de Eletrodomésticos em ${cityName}` :
                    `Serviços de Canalização em ${cityName}`}
          </h2>
          {isClimatizacaoSite && (
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
              Da pré-instalação em obra à manutenção anual, passando pela venda do equipamento
              e pelas peças de reposição. Um único interlocutor para toda a climatização.
            </p>
          )}
        </div>

        {isEletrodomesticosSite ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 flex flex-col hover:shadow-xl transition-shadow group">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-blue-600 shadow-sm">
                    DESDE
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="font-bold text-lg text-gray-900 mb-1 leading-tight">{service.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">{service.subtitle}</p>
                  <div className="mt-auto">
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-2xl font-black text-gray-900">{service.price.toFixed(2)} €</span>
                      <span className="text-[10px] text-gray-400 font-medium">+ IVA</span>
                    </div>
                    <button
                      onClick={() => openBooking(service)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm"
                    >
                      Pedir serviço
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Services with Image and Text (Original Layout) */
          services.map((service, index) => (
            <ServiceRow key={service.id} service={service} index={index} />
          ))
        )}

        {/* Principais problemas que resolvemos (oculto na climatização: lia-se como lista de palavras-chave) */}
        {!isClimatizacaoSite && (
        <div className="mt-6 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Principais problemas que resolvemos</h3>
          <p className="text-gray-600 text-center mb-6">
            {isCleanSite
              ? 'O que as pessoas procuram quando precisam de uma empresa de limpeza'
              : isClimatizacaoSite
              ? 'O que as pessoas procuram quando precisam de ar condicionado'
              : isEletricaSite
              ? 'Nossa ampla gama de serviços elétricos em Lisboa e arredores'
              : isDesentupimentosSite
                ? 'Termos que as pessoas pesquisam quando têm problemas com desentupimentos'
                : isEletrodomesticosSite
                  ? 'Especialistas na instalação de todo o tipo de eletrodomésticos'
                  : 'Termos que as pessoas pesquisam quando têm problemas com canalização'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {(isCleanSite ? [
              `empresa de limpeza ${cityName}`,
              'limpeza de condomínios',
              'limpeza de escadas de prédio',
              'limpeza pós-obra',
              'limpeza de escritórios',
              'limpeza doméstica',
              'empregada de limpeza',
              'limpeza profunda',
              'limpeza de mudança de casa',
              'limpeza de alojamento local',
              'limpeza Airbnb',
              'limpeza de restaurantes',
              'desengorduramento de cozinha',
              'limpeza de hottes',
              'limpeza de lojas',
              'limpeza de ginásios',
              'limpeza de vidros',
              'lavagem de garagens'
            ] : isClimatizacaoSite ? [
              `ar condicionado ${cityName}`,
              'instalação ar condicionado',
              'manutenção ar condicionado',
              'limpeza ar condicionado',
              'carga de gás ar condicionado',
              'ar condicionado não arrefece',
              'ar condicionado a pingar água',
              'venda ar condicionado',
              'multi-split',
              'ar condicionado de condutas',
              'bomba de calor',
              'pré-instalação ar condicionado',
              'ar condicionado para obra',
              'peças ar condicionado',
              'compressor ar condicionado',
              'comando ar condicionado',
              'quantos BTU preciso',
              'contrato de manutenção'
            ] : isDesentupimentosSite ? [
              'desentupimento urgente',
              'desentupimento Lisboa',
              'canalização entupida',
              'sanita entupida',
              'ralo entupido',
              'esgoto entupido',
              'pia entupida',
              'lavatório entupido',
              'coluna entupida',
              'desentupimento 24h',
              'desentupimento de esgotos',
              'desentupimento de sanitas',
              'desentupimento de pias',
              'hidrojato',
              'vídeo inspeção',
              'desentupimento profissional'
            ] : isEletricaSite ? [
              'Instalação Elétrica',
              'Quadro Elétrico',
              'Reparação Elétrica',
              'Iluminação LED',
              'Eletricista Urgente',
              'Aumento de Potência',
              'Automação Residencial',
              'Certificação Elétrica',
              'Tomadas e Interruptores',
              'Carregador Carro Elétrico',
              'Termo de Responsabilidade',
              'Eletricista 24 Horas',
              'Manutenção Elétrica',
              'Aterramento',
              'Diagnóstico de Avaria',
              'Ar Condicionado',
              'Vídeo Porteiro',
              'Wallbox Carregador',
              'Portões Automáticos',
              'Painéis Solares',
              'Bombas de Calor'
            ] : isEletrodomesticosSite ? [
              'instalação de placa',
              'instalação de forno',
              'instalação de fogão',
              'instalação de microondas',
              'instalação de máquina de lavar',
              'instalação de frigorífico',
              'instalação de termoacumulador',
              'instalação standard',
              'eletrodomésticos encastre',
              'instalação profissional',
              'eletricista para eletrodomésticos',
              'montagem de eletrodomésticos'
            ] : [
              'desentupimento urgente',
              'canalização entupida',
              'sanita entupida',
              'ralo entupido',
              'fuga de água',
              'detecção de fugas',
              'reparar fugas de água',
              'canos partidos',
              'torneira avariada',
              'autoclismo não funciona',
              'canalizador 24h',
              `desentupimento ${cityName}`,
              'reparação canalização',
              'instalação canalização',
              'remodelação casa de banho',
              'renovação WC'
            ]).map((item, idx) => (
              <span key={idx} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm border border-gray-200">
                {item}
              </span>
            ))}
          </div>
        </div>
        )}


        {/* Call to Action */}
        <div className="text-center mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-4">
            {isCleanSite ? 'Precisa de uma Empresa de Limpeza?' :
              isClimatizacaoSite ? 'Precisa de Ar Condicionado?' :
              isEletricaSite ? 'Precisa de um Eletricista de Confiança?' :
                isEletrodomesticosSite ? 'Precisa de uma Instalação Profissional?' :
                  'Precisa de Ajuda com Canalização?'}
          </h3>
          <p className="text-xl mb-8 text-blue-100">
            {isCleanSite
              ? 'Visita ao espaço e orçamento gratuitos. Proposta escrita com o valor mensal fechado.'
              : isClimatizacaoSite
              ? 'Visita técnica e orçamento gratuitos. Dizemos-lhe a potência certa antes de comprar.'
              : isEletrodomesticosSite
                ? 'Agende a sua instalação agora e garanta um serviço seguro'
                : 'Contacte-nos agora e receba um orçamento gratuito em menos de 24h'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={isClimatizacaoSite ? '#orcamento' : `tel:${siteConfig.phone}`}
              onClick={isClimatizacaoSite ? handleQuoteClick : handlePhoneClick}
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center gap-2"
            >
              <i className={isClimatizacaoSite ? 'ri-mail-send-line' : 'ri-phone-line'}></i>
              Pedir Orçamento
            </a>
            <a
              href={`tel:${siteConfig.phone}`}
              onClick={handlePhoneClick}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold py-4 px-8 rounded-lg transition-colors duration-200"
            >
              <i className="ri-phone-line mr-2"></i>
              {siteConfig.phoneDisplay}
            </a>
          </div>
        </div>
      </div>

      <ApplianceBookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        service={selectedService}
      />
    </section>
  );
};

export default Services;
